import { formatInUlaanbaatar } from "../utils/ulaanbaatarTime";
import { btlTypeLabel, rowMerchantMeta } from "./btlTypeLabel";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const text = (v) => (v == null || v === "" ? "" : String(v));

const joinUnique = (values) =>
  [...new Set(values.map((v) => text(v)).filter(Boolean))].join(", ");

const customerAccounts = (row) => {
  const list = Array.isArray(row.customers) ? row.customers : [];
  if (list.length) {
    return joinUnique(list.map((c) => c.accountNumber));
  }
  if (row.settle?.bucketAccountNumber) {
    return String(row.settle.bucketAccountNumber);
  }
  if (
    (row.direction === "OUT" || num(row.amount) < 0) &&
    row.relatedAccount
  ) {
    return String(row.relatedAccount);
  }
  return "";
};

const customerNames = (row) => {
  const list = Array.isArray(row.customers) ? row.customers : [];
  const names = list.map((c) => c.name).filter(Boolean);
  if (names.length) return joinUnique(names);
  if (row.settlementPartnerName) return String(row.settlementPartnerName);
  if (row.settle?.merchantName) return String(row.settle.merchantName);
  if (row.qpay?.merchantName) return String(row.qpay.merchantName);
  return "";
};

/** Хуучин transaction afterFee: item.amount - xpayFee - merchantFee */
export function qpayBreakdown(q, bankAmount) {
  if (!q) return null;
  const invoiceAmt = num(q.sumAmount || q.invoiceAmount || q.invoiceTotalAmount);
  const merchantFee = num(q.sumMerchantFee);
  const xpayFee = num(q.sumXpayFee);
  const qpayCharge = q.bankCharge != null ? num(q.bankCharge) : null;
  const bankNet =
    q.bankNetAmount != null
      ? num(q.bankNetAmount)
      : bankAmount > 0
        ? bankAmount
        : null;
  return {
    invoiceAmt,
    bankNet,
    merchantFee,
    xpayFee,
    qpayCharge,
    xpayMinusCharge:
      qpayCharge != null ? Number((xpayFee - qpayCharge).toFixed(2)) : null,
    subMerchantAfter: Number((invoiceAmt - merchantFee - xpayFee).toFixed(2)),
  };
}

/** qpay.items (invoice line enrich) → flat accounting invoiceItems */
export function mapAccountingInvoiceItems(q) {
  const list = Array.isArray(q?.items) ? q.items : [];
  return list.map((it) => ({
    product: text(it.productTypeName),
    accountNumber: text(it.accountNumber),
    amount: num(it.amount),
    merchantFee: num(it.merchantFee),
    xpayFee: num(it.xpayFee),
    afterFeeAmount: num(it.afterFeeAmount),
    txId: it.transactionId != null && it.transactionId !== "" ? String(it.transactionId) : "",
  }));
}

export const ACCOUNTING_PAYLOAD_KEYS = [
  "bankTransactionLogId",
  "transactionDate",
  "journalNo",
  "bankName",
  "ourAccountNo",
  "counterpartName",
  "counterpartAccount",
  "description",
  "income",
  "expense",
  "balance",
  "typeValue",
  "merchantId",
  "merchantName",
  "invoiceId",
  "paymentMethod",
  "itemCount",
  "product",
  "itemAccountNo",
  "txId",
  "invoiceAmount",
  "bankNetAmount",
  "merchantFee",
  "xpayFee",
  "qpayCharge",
  "xpayFeeMinusQpayCharge",
  "subMerchantAfter",
  "bucketId",
  "settleMerchantFee",
  "settleXpayFee",
  "settleAfter",
  "settleTxCount",
  "linkConfidence",
  "linkType",
  "invoiceItems",
];

/**
 * Enriched BTL row → canonical accounting object.
 * Excel headers and JSON keys are this object's keys.
 */
export function toAccountingPayload(row) {
  const amount = num(row.amount);
  const s = row.settle;
  const q = row.qpay;
  const invoiceItems = mapAccountingInvoiceItems(q);
  const b = qpayBreakdown(q, amount);
  const income = amount > 0 ? amount : 0;
  const expense = amount < 0 ? Math.abs(amount) : 0;
  const { merchantId, merchantName } = rowMerchantMeta(row);
  const typeValue =
    (row.typeValue != null && String(row.typeValue).trim()) ||
    btlTypeLabel(row) ||
    "";

  return {
    bankTransactionLogId: text(row.id),
    transactionDate: formatInUlaanbaatar(row.trnDate),
    journalNo: text(row.journalNo),
    bankName: text(row.bankName),
    ourAccountNo: text(row.accountNumber),
    counterpartName: customerNames(row),
    counterpartAccount: customerAccounts(row) || text(row.relatedAccount),
    description: text(row.description),
    income,
    expense,
    balance: row.remain != null && row.remain !== "" ? num(row.remain) : 0,
    typeValue,
    merchantId: merchantId != null ? String(merchantId) : "",
    merchantName: merchantName || "",
    invoiceId: row.invoiceId != null && row.invoiceId !== "" ? String(row.invoiceId) : "",
    paymentMethod: text(q?.paymentMethodName || q?.productPaymentType),
    itemCount: q ? num(q.itemCount || invoiceItems.length) : invoiceItems.length,
    product: joinUnique(invoiceItems.map((it) => it.product)),
    itemAccountNo: joinUnique(invoiceItems.map((it) => it.accountNumber)),
    txId: joinUnique(invoiceItems.map((it) => it.txId)),
    invoiceAmount: b ? b.invoiceAmt : 0,
    bankNetAmount: b && b.bankNet != null ? b.bankNet : 0,
    merchantFee: b ? b.merchantFee : 0,
    xpayFee: b ? b.xpayFee : 0,
    qpayCharge: b && b.qpayCharge != null ? b.qpayCharge : 0,
    xpayFeeMinusQpayCharge:
      b && b.xpayMinusCharge != null ? b.xpayMinusCharge : 0,
    subMerchantAfter: b ? b.subMerchantAfter : 0,
    bucketId:
      row.transactionBucketId != null && row.transactionBucketId !== ""
        ? String(row.transactionBucketId)
        : s?.transactionBucketId != null
          ? String(s.transactionBucketId)
          : "",
    settleMerchantFee: s ? num(s.sumMerchantFee) : 0,
    settleXpayFee: s ? num(s.sumXpayFee) : 0,
    settleAfter: s ? num(s.sumAfterFeeAmount) : 0,
    settleTxCount: s ? num(s.transactionCount) : 0,
    linkConfidence: text(row.linkConfidence || "NONE"),
    linkType: text(row.linkType || "NONE"),
    invoiceItems,
  };
}

export function toAccountingExcelRow(row) {
  const payload = toAccountingPayload(row);
  return {
    ...payload,
    invoiceItems: payload.invoiceItems.length
      ? JSON.stringify(payload.invoiceItems)
      : "",
  };
}

export function emptyAccountingExcelRow() {
  const row = {};
  for (const key of ACCOUNTING_PAYLOAD_KEYS) {
    row[key] = "";
  }
  return row;
}

export function buildAccountingExport(payload) {
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  return {
    accountNumber: payload.accountNumber,
    startDate: payload.startDate,
    endDate: payload.endDate,
    timezone: payload.timezone || "Asia/Ulaanbaatar",
    exportedAt: payload.exportedAt,
    totalCount: rows.length,
    rows: rows.map(toAccountingPayload),
  };
}

export function downloadBankTransactionLogJson(payload) {
  const data = buildAccountingExport(payload);
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const fileName = `BTL_${payload.accountNumber}_${payload.startDate}_${payload.endDate}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
